<script setup>
import { onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'dashboard/helper/toast';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import SlaMissedToast from './SlaMissedToast.vue';
import SlaMissedOverflowToast from './SlaMissedOverflowToast.vue';
import {
  useSlaMissedQueue,
  entryKey,
  shouldShowSlaMissedToast,
  conversationDisplayIdFromRoute,
  NOTIFICATIONS_VIEW_ROUTES,
} from './useSlaMissedQueue';

// Sem template: escuta a notificação sla_missed_* que chega pelo cable e abre
// um toast persistente no canto (Toaster global do Dashboard.vue). Fonte é o
// backend (SlaEvent#create_notifications, cron de 2 min) — NÃO recalcula prazo
// no cliente: a etiqueta âmbar/vermelha do card faz isso e já divergiu do
// relatório uma vez. Sem persistência: F5 zera; o histórico é o sino.
// Duas preferências do perfil mandam aqui: "Alertas na tela" (liga/desliga o
// toast) e "Alertas de áudio" (só o som).
//
// A fila (useSlaMissedQueue) é a fonte da verdade e decide o teto; o `sync`
// só espelha ela no sonner. Toast velho fecha sozinho quando a notificação é
// lida/adiada/apagada em qualquer aba (eventos do cable) ou quando a conversa
// é aberta na tela.

const queue = useSlaMissedQueue();
const route = useRoute();
const { uiSettings } = useUISettings();
const currentUserId = useMapGetter('getCurrentUserID');

// O que está aberto no sonner agora. Não reativo de propósito: é espelho do
// sonner, não estado de tela.
const openToastIds = new Set();
let overflowToast = null; // { id, count } do agregado aberto
let overflowSequence = 0;

const openIndividualToast = entry => {
  openToastIds.add(entry.id);
  toast.custom(SlaMissedToast, {
    id: entry.id,
    duration: Infinity,
    componentProps: {
      notification: entry.notification,
      metric: entry.metric,
      toastId: entry.id,
    },
    // Dispara também no dismiss programático (um frame depois): aí a entrada
    // já saiu da fila e o `remove` não acha nada.
    onDismiss: () => {
      openToastIds.delete(entry.id);
      queue.remove(entry.id);
    },
  });
};

const syncOverflowToast = () => {
  const count = queue.overflowCount.value;
  if (count === 0) {
    if (overflowToast) toast.dismiss(overflowToast.id);
    overflowToast = null;
    return;
  }
  if (overflowToast?.count === count) return;

  if (!overflowToast) {
    overflowSequence += 1;
    overflowToast = { id: `sla-missed-overflow-${overflowSequence}` };
  }
  overflowToast.count = count;
  const { id } = overflowToast;
  // Mesmo id = o sonner atualiza a contagem no lugar, sem empilhar outro.
  toast.custom(SlaMissedOverflowToast, {
    id,
    duration: Infinity,
    componentProps: { count, toastId: id },
    // Fechar o agregado (X ou "Ver notificações") descarta o excedente — os
    // alertas seguem no sino. Ignora o eco do dismiss que o próprio sync fez.
    onDismiss: () => {
      if (overflowToast?.id !== id) return;
      overflowToast = null;
      queue.clearOverflow();
    },
  });
};

// Alinha o sonner à fila: fecha o que saiu do teto, abre o que entrou (o mais
// antigo do excedente sobe quando um individual fecha) e o agregado por último.
const sync = () => {
  const visibleIds = new Set(queue.individual.value.map(entry => entry.id));
  openToastIds.forEach(id => {
    if (visibleIds.has(id)) return;
    openToastIds.delete(id);
    toast.dismiss(id);
  });
  queue.individual.value.forEach(entry => {
    if (!openToastIds.has(entry.id)) openIndividualToast(entry);
  });
  syncOverflowToast();
};

// Toda mudança da fila (alerta novo, toast fechado, evento do cable, rota)
// passa por aqui — os handlers abaixo só mexem na fila.
watch(queue.entries, sync);

const onSlaMissed = notification => {
  // Chave do atendente em Perfil → Notificações ("Alertas na tela"). Ausente =
  // ligado, pra que o deploy não silencie ninguém. Barra ANTES da fila: sem
  // entrada não há toast, som nem contagem no agregado. Silencia só a tela —
  // o sino segue registrando o estouro.
  if (uiSettings.value.sla_missed_alerts_disabled) return;

  // Só o responsável, e nada de FRT já respondido (regra no helper). Também
  // antes da fila: quem não vê o toast não ouve o som nem entra no agregado.
  if (!shouldShowSlaMissedToast(notification, currentUserId.value)) return;

  // `add` diz se é NOVO (dedup por conversa+métrica enquanto está aberto) —
  // só aí toca. Reenvio da mesma chave não abre nada.
  const entry = queue.add(notification);
  if (!entry) return;

  // Som segue a preferência "Alertas de áudio" do perfil (pode não tocar).
  DashboardAudioNotificationHelper.onSlaMissed(notification.primary_actor);
};

// Desligou "Alertas na tela" no perfil: limpa o que está aberto. Sem isto, quem
// desliga no meio de uma enxurrada continuaria com a pilha na tela pra fechar
// uma a uma — que é justamente o que o desligamento deveria resolver.
watch(
  () => uiSettings.value.sla_missed_alerts_disabled,
  disabled => {
    if (disabled) queue.removeAll();
  }
);

// Lida ou adiada (snooze) em outra aba, no app ou pela API: fecha a
// conversa+métrica daquela notificação. Leitura pelo sino do dashboard não
// passa aqui — ela vem como notification.read_all (update_all no backend).
const onNotificationUpdated = notification => {
  if (!notification?.read_at && !notification?.snoozed_until) return;
  queue.removeByKey(entryKey(notification));
};

const onNotificationDeleted = notification => {
  queue.removeByNotificationId(notification?.id);
};

// Sem primary actor = "marcar tudo como lido".
const onNotificationsReadAll = ({ primaryActorId }) => {
  if (primaryActorId) queue.removeByConversation(primaryActorId);
  else queue.removeAll();
};

// Abriu a conversa (lista, sino, caixa de entrada, link): o alerta dela já
// cumpriu o papel. Abriu a lista de notificações: o excedente está ali.
watch(
  () => route.fullPath,
  () => {
    const displayId = conversationDisplayIdFromRoute(route);
    if (displayId) queue.removeByConversationDisplayId(displayId);
    if (NOTIFICATIONS_VIEW_ROUTES.includes(route.name)) queue.clearOverflow();
  }
);

const BUS_HANDLERS = [
  [BUS_EVENTS.SLA_MISSED_NOTIFICATION, onSlaMissed],
  [BUS_EVENTS.NOTIFICATION_UPDATED, onNotificationUpdated],
  [BUS_EVENTS.NOTIFICATION_DELETED, onNotificationDeleted],
  [BUS_EVENTS.NOTIFICATIONS_READ_ALL, onNotificationsReadAll],
];

onMounted(() => {
  BUS_HANDLERS.forEach(([event, handler]) => emitter.on(event, handler));
});

onUnmounted(() => {
  BUS_HANDLERS.forEach(([event, handler]) => emitter.off(event, handler));
});
</script>

<template>
  <span class="hidden" />
</template>
